/**
 * Centralised access-token refresh.
 *
 * Two rules drive this module, both learned from the 2026-09-16 "users get
 * randomly logged out" incident:
 *
 * 1. Only the server may end a session. A refresh answered with 401/403 (or a
 *    missing token on our side) is a *definitive* failure and the caller must
 *    clear the session. Everything else - network error, timeout, 5xx (incl.
 *    the 503 the backend now returns for infrastructure failures) - is
 *    *transient* and must leave the session untouched.
 * 2. Concurrent 401s must trigger exactly ONE refresh. Pages that poll with
 *    several parallel requests used to fire a refresh per failed request.
 *
 * The response envelope is `{ success, message, result: { token } }`
 * (see the backend's authController.refreshToken); `data` is tolerated as a
 * defensive fallback only.
 */
import axios from 'axios';
import { runtimeConfig } from '../config/runtime';
import { SessionManager } from '../utils/sessionManager';

const REFRESH_URL = `${runtimeConfig.apiUrl}/api/auth/refresh`;

/** Error thrown by {@link refreshAccessToken}, carrying the logout verdict. */
export class RefreshFailedError extends Error {
  /** True only when the server rejected the token itself (401/403) or we had none. */
  definitive: boolean;
  /** The underlying axios/other error, kept for logging. */
  cause?: unknown;

  constructor(definitive: boolean, cause?: unknown) {
    super(
      definitive
        ? 'Session refresh was rejected by the server'
        : 'Session refresh failed temporarily'
    );
    this.name = 'RefreshFailedError';
    this.definitive = definitive;
    this.cause = cause;
  }
}

function readToken(container: unknown): string | null {
  if (!container || typeof container !== 'object') {
    return null;
  }
  const { token } = container as { token?: unknown };
  return typeof token === 'string' && token.length > 0 ? token : null;
}

/**
 * Pull the refreshed token out of a `/auth/refresh` response body.
 * Reads `result.token` (the real backend shape) and falls back to `data.token`.
 */
export function extractRefreshedToken(body: unknown): string | null {
  if (!body || typeof body !== 'object') {
    return null;
  }
  const envelope = body as { result?: unknown; data?: unknown };
  return readToken(envelope.result) ?? readToken(envelope.data);
}

/**
 * True only when the server explicitly said the credentials are invalid.
 * A missing `response` (network error / timeout) or any non-401/403 status is
 * transient and must NOT end the session.
 */
export function isDefinitiveAuthFailure(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }
  if (error instanceof RefreshFailedError) {
    return error.definitive;
  }
  const status = (error as { response?: { status?: unknown } }).response?.status;
  return status === 401 || status === 403;
}

async function performRefresh(): Promise<string> {
  const currentToken = SessionManager.getToken();
  if (!currentToken) {
    throw new RefreshFailedError(true, new Error('No access token to refresh'));
  }

  let body: unknown;
  try {
    // Raw axios on purpose: the shared instance's interceptor would recurse.
    const response = await axios.post(
      REFRESH_URL,
      { token: currentToken },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${currentToken}`,
        },
        withCredentials: true,
      }
    );
    body = response?.data;
  } catch (error) {
    throw new RefreshFailedError(isDefinitiveAuthFailure(error), error);
  }

  const token = extractRefreshedToken(body);
  if (!token) {
    // A 2xx we cannot read is a contract drift, not a verdict on the token -
    // treat it as transient so a shape change can never log everyone out again.
    console.warn('Token refresh returned no token; keeping the current session.');
    throw new RefreshFailedError(false, new Error('Refresh response contained no token'));
  }

  SessionManager.setToken(token);
  return token;
}

let inFlightRefresh: Promise<string> | null = null;

/**
 * Refresh the access token, storing it on success.
 * Concurrent callers share a single in-flight request.
 *
 * @throws {RefreshFailedError} `definitive` tells the caller whether to log out.
 */
export function refreshAccessToken(): Promise<string> {
  if (!inFlightRefresh) {
    inFlightRefresh = performRefresh().finally(() => {
      inFlightRefresh = null;
    });
  }
  return inFlightRefresh;
}

/** Test-only: drop the shared in-flight promise between cases. */
export function __resetRefreshStateForTests(): void {
  inFlightRefresh = null;
}
